import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Linkify from 'react-linkify';
import {
  head,
  flatten,
  isEqual,
  startCase,
} from 'lodash';
import prettierBytes from 'prettier-bytes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';

import {
  ERROR_CODES,
  ERROR_USER_FACING_NAME_BY_DOKKIO_CLASS,
  MAX_DOKKIO_USERS_LIMIT_ERROR_MESSAGE,
  MAX_LOGO_LIMIT_ERROR_MESSAGE,
  MAX_STARRED_OBJECTS_PER_DOKKIO_USER_LIMIT_ERROR_MESSAGE,
  SEGMENT_PAGE_CONTEXTS,
  PROVIDER_NAME_BY_CREDENTIAL_OAUTH_TYPE,
} from '../constants';

import { MAX_DOKKIO_USER_COUNT_PER_ACCOUNT, USER_FACING_TEXT_BY_INTEGRATION_TYPE } from '../constants-shared';

import { Alert, EllipsedParagraph } from '../ui';

import MUTATION_LOG_APP_ERROR from '../queries/mutationLogAppError';

import withData from '../decorators/withData';

import { track } from '../services/Analytics';

import usePrevious from '../hooks/usePrevious';

import getIndefiniteArticle from '../utils/getIndefiniteArticle';
import getCreateAccountURL from '../utils/getCreateAccountURL';
import pluralCount from '../utils/pluralCount';

import AccountLink from './links/AccountLink';
import { DataContext } from './Data';
import MainDisplayObjectLink from './links/MainDisplayObjectLink';

export const GENERIC_MESSAGE = 'Dokkio has encountered an error.';

const USER_FACING_ACTION_BY_ACTION = {
  RenameItem: 'Renaming',
  CreateFolder: 'Adding a folder',
  UploadFile: 'Upload',
  DeleteItem: 'Deleting',
  CopyItem: 'Copying',
  SyncCollapsedIntegrationItems: 'Item Sync',
  DownloadRemoteFile: 'Downloading files',
  GetRemoteItemPermissions: 'Item permissions',
  UpdatePermission: 'Updating permissions',
  DeletePermission: 'Deleting permissions',
  CreatePermission: 'Adding permissions',
};

const USER_FACING_VERB_BY_ACTION = {
  DeleteItem: 'delete',
  CopyItem: 'copy',
  CreateFile: 'add a file in',
  CreateFolder: 'add a folder in',
  RenameItem: 'rename',
  DownloadRemoteFile: 'download',
  DoesSharedPublicLinkExist: 'view permission details for',
  GetSharedPublicLinkInfo: 'view permission details for',
  CreateSharedPublicLink: 'generate a public link for',
  RevokeSharedPublicLink: 'delete the public link for',
  GetRemoteItem: 'view details for',
  GetRemoteDownloadPlan: 'download',
  GetRemoteItemPermissions: 'view permissions for',
  MoveItem: 'move',
  UpdatePermission: 'update permissions for',
  DeletePermission: 'delete permissions for',
  CreatePermission: 'add permissions for',
  UploadFile: 'upload',
};

export const ErrorMessage = ({
  displayMode,
  errors,
  renderDescription,
  segmentPageCtxt,
}) => {
  const {
    dokkioLabelByID = {},
    logAppError,
  } = useContext(DataContext);
  const { goBack } = useHistory();
  const messageRef = useRef(null);
  const prevErrors = usePrevious(errors);

  const displayErrors = flatten(errors
    .filter(Boolean)
    .map(error => error.graphQLErrors || error));
  const firstDisplayError = head(displayErrors);

  const scrollIntoView = () => {
    if (messageRef.current && messageRef.current.scrollIntoView) {
      messageRef.current.scrollIntoView();
    }
  };

  const logErrorBoundaryMessage = async () => {
    if (displayMode === 'errorBoundary' && firstDisplayError) {
      const message = firstDisplayError.stack ? firstDisplayError.stack : firstDisplayError;
      track('DokkioError Shown', { message });
      await logAppError({
        variables: {
          message,
          extraAttributes: {
            href: window.location.href,
            user_agent: navigator.userAgent,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (firstDisplayError) {
      scrollIntoView();
    }
    logErrorBoundaryMessage();
  }, []);

  useEffect(() => {
    if (!isEqual(errors, prevErrors)
      && firstDisplayError) {
      scrollIntoView();
    }
  }, [errors]);

  /* eslint-disable react/prop-types */
  const errorMessageByCode = {
    [ERROR_CODES.CREDENTIAL_PREFLIGHT_ERROR]: ({
      message,
      credentialOAuthType,
    }) => {
      const providerName = credentialOAuthType
        ? PROVIDER_NAME_BY_CREDENTIAL_OAUTH_TYPE[credentialOAuthType] : null;
      return (
        <React.Fragment>
          <p>Dokkio encountered a problem connecting to your{providerName ? ` ${providerName}` : ''} account.</p>
          <p>Try using a different account, or asking your administrator to enable API access, or contact <Linkify className="dokkio-link">support@dokkio.com</Linkify> for assistance.</p>
          <p>The error from {providerName || 'the account'} is: {message}</p>
        </React.Fragment>
      );
    },
    [ERROR_CODES.DESKTOP_OFFLINE]: 'Your desktop where these files are stored is not online with the Dokkio app running',
    [ERROR_CODES.DESKTOP_TIMEOUT]: 'Your desktop where these files are stored is not responding, make sure it is online and the Dokkio app is running',
    [ERROR_CODES.INVALID_SUBSCRIPTION]: 'Your Dokkio subscription has expired',
    [ERROR_CODES.INSUFFICIENT_LICENSED_SEATS]: ({
      licensedUserCount,
      currentUserCount,
    }) => (
      <React.Fragment>
        You do not have enough licenses for that. You are licensed for{' '}
        {pluralCount(licensedUserCount, 'users')}, and already have{' '}
        {pluralCount(currentUserCount, 'users')}.{' '}
        <AccountLink to="/accountSettings/subscription">Add more seats</AccountLink>.
      </React.Fragment>
    ),
    [ERROR_CODES.INCOMPLETE_USER_ACCOUNT]: (
      <React.Fragment>
        <p>You do not have a Dokkio password set up.</p>
        <p>Use &quot;Forgot Password&quot; to set up a password.</p>
        <p>Or login with Google, Facebook or other 3rd party authentication.</p>
      </React.Fragment>
    ),
    [ERROR_CODES.INVALID_EMAIL_OR_PASSWORD]: 'Username & Password are not valid for this account',
    [ERROR_CODES.INVALID_INVITATION]: 'No incomplete user with that info for this account',
    [ERROR_CODES.INVALID_RESET_URL]: 'We could not authenticate you at this time. Your reset link could have been copied incorrectly. Please try resending reset email.',
    [ERROR_CODES.UNIQUE_VIOLATION]: ({
      dokkioClass,
      dokkioLabelID,
      fieldNames = [],
      integrationID,
      integrationItemType,
      name,
      primaryEmail,
      remoteID,
      email,
      slug,
      ...variables
    }) => {
      let dokkioClassString;
      if (dokkioClass === 'dokkio_tag') {
        const dokkioLabel = dokkioLabelByID[dokkioLabelID];
        dokkioClassString = dokkioLabel ? dokkioLabel.nameSingular : 'Basic Tag';
      } else if (dokkioClass === 'integration_item' && integrationItemType) {
        dokkioClassString = integrationItemType.replace('integration_', '');
      } else {
        dokkioClassString = ERROR_USER_FACING_NAME_BY_DOKKIO_CLASS[dokkioClass]
          || startCase(dokkioClass);
      }

      if ((dokkioClass === 'dokkio_user' && fieldNames.includes('primaryEmail'))
        || (dokkioClass === 'remote_user' && fieldNames.includes('email'))) {
        return `The email address '${primaryEmail || email}' is already being used by another user on this account.`;
      }

      let nameString;
      if (dokkioClass === 'integration_item') {
        nameString = (
          <MainDisplayObjectLink
            mainDisplayObject={{
              dokkioClass,
              integrationID,
              remoteID,
              integrationItemType,
              name,
              ...variables,
            }}
            segmentClickCtxt="ErrorMessage"
            segmentPageCtxt={segmentPageCtxt}
          />
        );
      } else if (name) {
        nameString = `named ${name}`;
      }
      if (nameString) {
        const articleString = getIndefiniteArticle(dokkioClassString);
        return (
          <EllipsedParagraph>
            There is already {articleString} {dokkioClassString} {nameString}. Please delete the
            existing {dokkioClassString} if you want to do this.
          </EllipsedParagraph>
        );
      }
      if (fieldNames.includes('slug')) {
        return `There is already an account with the URL '${slug}'. Please choose another one.`;
      }
      if (dokkioClass === 'credential_oauth') {
        return `This ${dokkioClassString} has already been added to this account by another user.`;
      }
      if (dokkioClass === 'integration' && name) {
        return `Duplicate content source name '${name}' is not allowed`;
      }
      return null;
    },
    [ERROR_CODES.NOT_FOUND]: ({ dokkioClass }) => {
      const name = ERROR_USER_FACING_NAME_BY_DOKKIO_CLASS[dokkioClass] || startCase(dokkioClass);
      const notFoundMessage = `${name} not found`;
      if (dokkioClass === 'account') {
        return (
          <React.Fragment>
            {notFoundMessage}
            <p className="text-xs">
              Dokkio can&apos;t find an account with that name. Do you want to
              {' '}
              <a className="dokkio-link" href={getCreateAccountURL()}>Create a New Account</a> instead?
            </p>
            <p className="text-xs">
              If you can&apos;t remember your account name, email
              {' '}
              <Linkify className="dokkio-link">support@dokkio.com</Linkify>
            </p>
          </React.Fragment>
        );
      }
      return notFoundMessage;
    },
    [ERROR_CODES.INVALID_CREDENTIAL]: ({ client }) => {
      const providerText = USER_FACING_TEXT_BY_INTEGRATION_TYPE[client];
      return `Linked ${providerText} account authentication is invalid. Please navigate to content sources screen to repair.`;
    },
    [ERROR_CODES.TOO_MANY_REQUESTS]: ({
      client,
      email,
    }) => {
      let actionText = 'actions';
      if (client) {
        const clientString = client === 'google_drive' ? 'google drive' : client;
        actionText = `operations on ${clientString}`;
      } else if (email) {
        actionText = `logins for '${email}'`;
      }
      return `You've attempted too many ${actionText} in too short of time. Please try again after a minute.`;
    },
    [ERROR_CODES.NOT_IMPLEMENTED]: ({
      action,
      integrationType,
    }) => `${USER_FACING_ACTION_BY_ACTION[action]} is not available for ${USER_FACING_TEXT_BY_INTEGRATION_TYPE[integrationType]}.`,
    [ERROR_CODES.INSUFFICIENT_PROVIDER_PERMISSIONS]: ({
      action,
    }) => {
      const actionVerb = USER_FACING_VERB_BY_ACTION[action] || 'do this to';

      return `You do not have sufficent permission to ${actionVerb} this item.`;
    },
    [ERROR_CODES.INSUFFICIENT_DOKKIO_PERMISSIONS]: ({
      capability,
      dokkioClass,
      id,
      namePlural,
      primaryDisplayName,
    }) => {
      let objectDescription = namePlural;
      if (dokkioClass === 'dokkio_user' && primaryDisplayName) {
        objectDescription = `the user ${primaryDisplayName}`;
      }
      if (!objectDescription) {
        objectDescription = `id '${id}'`;
      }
      let capabilityDescription;
      if (capability === 'canEdit' || capability === 'canDelete') {
        capabilityDescription = `${capability.substr(3).toLowerCase()} ${objectDescription}`;
      } else if (dokkioClass === 'account' && capability.startsWith('canCreate')) {
        capabilityDescription = `add a ${startCase(capability.substr(9)).toLowerCase()} on this account`;
      }
      return `You do not have sufficient permission to ${capabilityDescription}, please contact your administrator`;
    },
    [ERROR_CODES.MAX_FILE_SIZE_OPERATION]: ({
      action,
      name,
      fileSize,
      maxSize,
    }) => {
      const actionDescriptionByAction = {
        copy: 'copied',
        move: 'moved to other integrations',
      };
      return `'${name}' is ${prettierBytes(parseInt(fileSize, 10))}, only files less than
        ${prettierBytes(parseInt(maxSize, 10))} can be ${actionDescriptionByAction[action]}`;
    },
    [ERROR_CODES.TOO_MANY_LICENSED_USERS]: `Please contact support for subscriptions over ${MAX_DOKKIO_USER_COUNT_PER_ACCOUNT} seats`,
    [ERROR_CODES.TOO_MANY_OBJECTS]: ({
      action,
      count,
    }) => {
      const actionDescriptionByAction = {
        recursiveSelect: `There are too many recursive items selected. Try selecting fewer than ${count} total items`,
        update: `There are too many items selected to support updating them at one time. Try selecting fewer than ${count} total items`,
      };
      return actionDescriptionByAction[action] || 'Try selecting fewer items';
    },
    [ERROR_CODES.TOO_MANY_INSTANCES]: ({ dokkioClass }) => {
      switch (dokkioClass) {
        case 'dokkio_user':
          return MAX_DOKKIO_USERS_LIMIT_ERROR_MESSAGE;
        case 'logo':
          return MAX_LOGO_LIMIT_ERROR_MESSAGE;
        case 'starred_object':
          return MAX_STARRED_OBJECTS_PER_DOKKIO_USER_LIMIT_ERROR_MESSAGE;
        default:
          return null;
      }
    },
    [ERROR_CODES.UNSUPPORTED_LOGO_FORMAT]: 'That file type cannot be used. Only JPGs, GIFs, and PNGs can be used.',
  };
  /* eslint-enable react/prop-types */

  const defaultMessageContent = (
    <p>
      Try <a className="dokkio-link" onClick={window.location.reload} onKeyPress={window.location.reload} tabIndex={0} role="button">refreshing the page</a>
      {' '}or <a className="dokkio-link" onClick={goBack} onKeyPress={goBack} tabIndex={0} role="button">going back</a>
      {' '}and trying the action again, if the problem persists
      {' '}<a className="dokkio-link" href="mailto:support@dokkio.com">contact support</a>
    </p>
  );

  const renderError = (error) => {
    if (typeof error === 'string' || (typeof error === 'object' && !error.message)) {
      return error;
    }

    if (error.code && errorMessageByCode[error.code]) {
      const errorMessage = errorMessageByCode[error.code];
      const errorParams = {
        ...(error.variables || {}),
        message: error.message,
      };
      const userFriendlyMessage = typeof errorMessage === 'function' ? errorMessage(errorParams) : errorMessage;
      if (userFriendlyMessage) {
        return userFriendlyMessage;
      }
    }

    if (error.message) {
      return error.message;
    }

    return null;
  };

  if (!firstDisplayError) {
    return null;
  }

  if (displayMode === 'text') {
    return renderError(firstDisplayError) || GENERIC_MESSAGE;
  }
  if (displayMode === 'alert') {
    return (
      <Alert ref={messageRef} alertStyle="error">
        {renderError(firstDisplayError) || GENERIC_MESSAGE}
      </Alert>
    );
  }

  return (
    <div className="flex w-4/5 mx-auto my-10 space-x-8">
      <FontAwesomeIcon className="w-24 h-24" icon={faExclamationTriangle} />
      <div className="space-y-2.5">
        <h2 className="text-2xl">{GENERIC_MESSAGE}</h2>
        <div>{renderError(firstDisplayError)}</div>
        <div className="text-xs">
          {renderDescription(displayErrors) || defaultMessageContent}
        </div>
      </div>
    </div>
  );
};

ErrorMessage.propTypes = {
  displayMode: PropTypes.oneOf(['alert', 'view', 'text', 'errorBoundary']),
  errors: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      code: PropTypes.string,
      variables: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    }),
    PropTypes.node,
    PropTypes.string,
  ])),
  renderDescription: PropTypes.func,
  segmentPageCtxt: PropTypes.oneOf(SEGMENT_PAGE_CONTEXTS),
};

ErrorMessage.defaultProps = {
  displayMode: 'alert',
  errors: [],
  renderDescription: () => null,
  segmentPageCtxt: null,
};

export default withData({
  mutations: [MUTATION_LOG_APP_ERROR],
})(ErrorMessage);
